import "./App.scss";
import About from "./pages/about";
import Contact from "./pages/contact";
import Events from "./pages/events";
import { Routes, Route, useLocation } from "react-router-dom";
import Footer from "./components/footer";
import Video from "./pages/video";
import Donate from "./pages/donate";
import MobileHomePage from "./pages/mobileHomePage";
import { useEffect, useState, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { fetchCalendarEvents, formatEventForDisplay } from "./services/calendarService";

// SEO helper function to update page metadata
const updatePageMetadata = (title, description) => {
  document.title = title
    ? `${title} | Bat City Brass Band`
    : "Bat City Brass Band";

  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  }
};

function App() {
  const isMobile = window.innerWidth < 768;
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);
  const hasInitialized = useRef(false);

  // Check for upcoming events on app load - single API call
  useEffect(() => {
    // Prevent duplicate calls (React Strict Mode can cause double execution)
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const checkEvents = async () => {
      try {
        setEventsLoading(true);
        setEventsError(null);
        
        const result = await fetchCalendarEvents({ maxResults: 20, upcomingOnly: true });
        
        if (result.success) {
          const formattedEvents = result.events.map(formatEventForDisplay);
          setEvents(formattedEvents);
        } else {
          setEventsError(result.error || 'Failed to load events');
          setEvents([]);
          console.error('App: Failed to load events:', result.error);
        }
      } catch (error) {
        console.error('App: Error checking for events:', error);
        setEventsError(error.message || 'An unexpected error occurred');
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    };

    checkEvents();
  }, []);

  // Update metadata when route changes
  useEffect(() => {
    const path = location.pathname;

    switch (path) {
      case "/about":
        updatePageMetadata(
          "About",
          "Learn about Bat City Brass Band - Austin's premier brass band playing high-energy dance music with international influences."
        );
        break;
      case "/contact":
        updatePageMetadata(
          "Contact",
          "Book Bat City Brass Band for your next event in Austin. Reach out to us for performances, private events, and more."
        );
        break;
      case "/events":
        updatePageMetadata(
          "Events",
          "Upcoming Bat City Brass Band events and performances in Austin. Don't miss our high-energy brass band shows!"
        );
        break;
      case "/video":
        updatePageMetadata(
          "Videos",
          "Watch Bat City Brass Band performances. See our high-energy brass band music in action at various Austin venues and events."
        );
        break;
      case "/donate":
        updatePageMetadata(
          "Support Us",
          "Support Bat City Brass Band. Your donations help us continue bringing vibrant brass music to Austin and beyond."
        );
        break;
      default:
        updatePageMetadata(
          "",
          "Bat City Brass Band - Austin's premier brass band playing high-energy dance music with international influences."
        );
        break;
    }
  }, [location]);

  return (
    <HelmetProvider>
      <div className="App">
        <Routes>
          <Route path="/" element={isMobile ? <MobileHomePage showEventsNav={events.length > 0} /> : <></>} />
          <Route path="/about" element={<About isMobile={isMobile} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/events" element={<Events events={events} loading={eventsLoading} error={eventsError} />} />
          <Route path="/video" element={<Video />} />
          <Route path="/donate" element={<Donate />} />
        </Routes>
        <Footer isMobile={isMobile} showEventsNav={events.length > 0} />
      </div>
    </HelmetProvider>
  );
}

export default App;
